import React from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

/**
 * Center-text plugin: draws "TOTAL" + sum inside the donut hole.
 * Registered once globally.
 */
const centerTextPlugin = {
  id: "donutCenterText",
  beforeDraw(chart) {
    const { ctx, width, height } = chart;
    const meta = chart.getDatasetMeta(0);
    if (!meta || !meta.data || meta.data.length === 0) return;

    const dataset = chart.data.datasets[0];
    if (!dataset) return;

    const total = dataset.data.reduce((a, b) => a + (Number(b) || 0), 0);

    ctx.save();
    const centerX = width / 2;
    const centerY = height / 2 - 10; // nudge up to account for legend

    // "TOTAL" label
    ctx.font = `600 11px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    ctx.fillStyle = "#6B7280";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText("TOTAL", centerX, centerY - 10);

    // Total number
    ctx.font = `700 28px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif`;
    ctx.fillStyle = "#1F2937";
    ctx.fillText(total.toLocaleString(), centerX, centerY + 16);

    ctx.restore();
  },
};

ChartJS.register(centerTextPlugin);

/**
 * Reusable Donut chart component with center total text.
 *
 * Props:
 *  - data        : Chart.js data object (labels, datasets)
 *  - options     : (optional) Chart.js options override
 *  - height      : (optional) container height, default "260px"
 *  - showLegend  : (optional) show legend, default true
 */
function DonutChart({
  data,
  options: optionsOverride,
  height = "260px",
  showLegend = true,
}) {
  // Make data donut-friendly: no border between segments, wider ring
  const donutData = {
    ...data,
    datasets: (data.datasets || []).map((ds) => ({
      ...ds,
      borderWidth: 0,
      hoverBorderWidth: 2,
      hoverOffset: 6,
      borderRadius: 2,
    })),
  };

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "68%",
    plugins: {
      legend: {
        display: showLegend,
        position: "bottom",
        labels: {
          boxWidth: 12,
          boxHeight: 12,
          usePointStyle: true,
          pointStyle: "circle",
          font: { size: 12, weight: 500 },
          color: "#4B5563",
          padding: 14,
        },
      },
      tooltip: {
        backgroundColor: "rgba(17, 24, 39, 0.95)",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 12,
        borderColor: "rgba(255, 255, 255, 0.1)",
        borderWidth: 1,
        displayColors: true,
        usePointStyle: true,
        boxPadding: 6,
        callbacks: {
          label(context) {
            const label = context.label || "";
            const value = context.parsed || 0;
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
            return ` ${label}: ${value} (${pct}%)`;
          },
        },
      },
    },
  };

  // Deep merge user overrides
  const mergedOptions = optionsOverride
    ? {
        ...defaultOptions,
        ...optionsOverride,
        plugins: {
          ...defaultOptions.plugins,
          ...(optionsOverride.plugins || {}),
          legend: {
            ...defaultOptions.plugins.legend,
            ...(optionsOverride.plugins?.legend || {}),
          },
          tooltip: {
            ...defaultOptions.plugins.tooltip,
            ...(optionsOverride.plugins?.tooltip || {}),
          },
        },
      }
    : defaultOptions;

  return (
    <div style={{ height, position: "relative" }}>
      <Doughnut data={donutData} options={mergedOptions} />
    </div>
  );
}

export default DonutChart;
